import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api-fetch";
import { ApiError } from "@/lib/api-error";
import { reportApiError } from "@/lib/report-error";
import type { UserProfile } from "@/types/user-profile";

interface Agencia {
  id: string;
  nome: string;
}

interface CreateUserModalProps {
  profiles: UserProfile[];
  onSuccess: () => void;
}

export function CreateUserModal({ profiles, onSuccess }: CreateUserModalProps) {
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [profileId, setProfileId] = useState<string>("none");
  const [agenciaId, setAgenciaId] = useState<string>("none");
  const [agencias, setAgencias] = useState<Agencia[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Verifica se o perfil selecionado é de agência
  const selectedProfile = profiles.find((p) => p.id.toString() === profileId);
  const isAgencyProfile = selectedProfile?.slug?.includes("agencia") ?? false;

  // Buscar agências quando necessário
  useEffect(() => {
    if (isAgencyProfile && agencias.length === 0) {
      supabase
        .from("agencias")
        .select("id, nome")
        .order("nome")
        .then(({ data }) => {
          if (data) setAgencias(data);
        });
    }
  }, [isAgencyProfile, agencias.length]);

  // Reset agencia quando muda perfil
  useEffect(() => {
    if (!isAgencyProfile) {
      setAgenciaId("none");
    }
  }, [isAgencyProfile]);

  const resetForm = () => {
    setFullName("");
    setEmail("");
    setPassword("");
    setProfileId("none");
    setAgenciaId("none");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isAgencyProfile && agenciaId === "none") {
      toast({
        title: "Erro",
        description: "Selecione uma agência para perfis de agência.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const body: Record<string, unknown> = {
        email,
        password,
        full_name: fullName,
      };

      if (profileId && profileId !== "none") {
        body.profile_id = parseInt(profileId);
      }

      if (isAgencyProfile && agenciaId !== "none") {
        body.agencia_id = agenciaId;
      }

      await apiFetch(`${SUPABASE_URL}auth/v1/admin/create-user`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      toast({
        title: "Sucesso",
        description: "Usuário criado com sucesso!",
      });

      resetForm();
      setOpen(false);
      onSuccess();
    } catch (error) {
      reportApiError(error, { source: "users", kind: "create" });
      console.error("Erro ao criar usuário:", error);

      if (error instanceof ApiError && error.status === 409) {
        toast({
          title: "Erro",
          description: "Já existe um usuário com este email.",
          variant: "destructive",
        });
        return;
      }

      let message = "Não foi possível criar o usuário.";
      if (error instanceof ApiError && error.body) {
        try {
          const parsed = JSON.parse(error.body);
          if (typeof parsed?.detail === "string") message = parsed.detail;
        } catch {
          // body não é JSON
        }
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }

      toast({ title: "Erro", description: message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeProfiles = profiles.filter((p) => p.is_active);

  return (
    <Dialog open={open} onOpenChange={(value) => {
      setOpen(value);
      if (!value) resetForm();
    }}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Novo Usuário
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Criar Novo Usuário</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">Nome Completo</Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Nome completo do usuário"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@exemplo.com"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Senha inicial"
              required
              minLength={6}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile">Perfil de Acesso</Label>
            <Select value={profileId} onValueChange={setProfileId}>
              <SelectTrigger id="profile">
                <SelectValue placeholder="Selecione um perfil" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem perfil</SelectItem>
                {activeProfiles.map((profile) => (
                  <SelectItem key={profile.id} value={profile.id.toString()}>
                    {profile.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isAgencyProfile && (
            <div className="space-y-2">
              <Label htmlFor="agencia">Agência *</Label>
              <Select value={agenciaId} onValueChange={setAgenciaId}>
                <SelectTrigger id="agencia">
                  <SelectValue placeholder="Selecione uma agência" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Selecione...</SelectItem>
                  {agencias.map((agencia) => (
                    <SelectItem key={agencia.id} value={agencia.id}>
                      {agencia.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Criando..." : "Criar Usuário"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
